import { type UseFormReturnType } from '@mantine/form';
import { useEffect } from 'react';
import { getRecentlyCompletedMonth } from '../../../../helpers/general-helpers';
import { Charts as ChartsType } from '../../../../store/financials-store/financials-store.type';

/**
 * Syncs Zustand store dates into the Mantine form's dateRange field.
 * Adjusts end date to last completed month if it falls in the current month
 * (except for AgentCosts charts which allow current month).
 *
 * IMPORTANT: deps must NOT include form.setFieldValue or form.resetDirty.
 * Although both methods are useCallback-wrapped individually (Mantine PR #7029),
 * the `form` object itself is a new literal every render (use-form.cjs line 253).
 * The React Compiler detects `form.resetDirty` as a property access on an unstable
 * object and re-evaluates the dependency on every render, causing infinite loops.
 *
 * @param {UseFormReturnType<{ dateRange: [Date | null, Date | null] }>} form - Mantine form instance with dateRange field
 * @param {Date | string | null} startDate - Start date from Zustand store (ISO string or Date)
 * @param {Date | string | null} endDate - End date from Zustand store (ISO string or Date)
 * @param {ChartsType} selectedChart - Currently selected chart type
 */
function useDateSync(
  form: UseFormReturnType<{ dateRange: [Date | null, Date | null] }>,
  startDate: Date | string | null,
  endDate: Date | string | null,
  selectedChart: ChartsType,
) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: form.setFieldValue and form.resetDirty are individually stable (useCallback-wrapped), but accessed via `form` which is a new object every render. The React Compiler treats `form.x` as an unstable dep, causing infinite loops when included.
  useEffect(() => {
    const newStartDate =
      typeof startDate === 'string' ? new Date(startDate) : startDate instanceof Date ? startDate : null;
    let newEndDate = typeof endDate === 'string' ? new Date(endDate) : endDate instanceof Date ? endDate : null;

    if (newEndDate) {
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const endMonth = newEndDate.getMonth();
      const endYear = newEndDate.getFullYear();

      if (endMonth === currentMonth && endYear === currentYear && selectedChart !== ChartsType.AgentCosts) {
        newEndDate = getRecentlyCompletedMonth();
      }
    }

    form.setFieldValue('dateRange', [newStartDate, newEndDate]);
    form.resetDirty();
  }, [startDate, endDate, selectedChart]);
}

export { useDateSync };
